import {pick} from '@benchling/js-utils';
import {Text} from '@mantine/core';
import React, {useEffect} from 'react';
import isEqual from 'react-fast-compare';

import {BaseSeedDataConfig, SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import BenchlingConfig from 'BenchlingConfig';
import {useAnalysisSourcesDuringExecution} from 'procedures/design/analysis-template-config/useAnalysisSources';
import {
  PlateDatasetType,
  PlateDesignSpec,
  PlateRecordSpec,
  AnalysisSourcesOrigin,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {ProcedureAnalysisTemplateConfiguration} from 'procedures/ProcedureDomainTypes';
import ProcedureMethodAnalysisSourceTable, {
  SelectedSources,
} from 'procedures/shared-components/analysis/ProcedureMethodAnalysisSourceTable';
import {
  getPlateDesignSourceIdentifier,
  getPlateRecordSourceIdentifier,
  parsePlateDesignSourceIdentifier,
  parsePlateRecordSourceIdentifier,
  parsePlateSourceIdentifier,
} from 'procedures/shared-components/analysis/procedureMethodAnalysisUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import FieldLabel from 'ui-library/inputs/FieldLabel';

export type WorksheetSeedDataConfig = BaseSeedDataConfig<
  SeedDataConfigType.WORKSHEET,
  {
    worksheetId: string;
    // Timestamp matching timestamp in default analysis name
    sentToAnalysisTimestamp: string;
    shouldIncludeConditionsDataFrame: boolean;
    shouldIncludeDataParameterDataFrame: boolean;
    shouldIncludeMaterialInputDataFrame: boolean;
    shouldIncludeMaterialOutputDataFrame: boolean;
    shouldIncludeEquipmentDataFrame: boolean;
    shouldIncludeTestOrderDataFrame: boolean;
    shouldIncludePreviousTestOrderDataFrame: boolean;
    // availablePlateIdsToSummarize will be filtered on the backend to only plates with schema IDs in plateSchemaIdsToSummarize
    plateSchemaIdsToSummarize: Array<string>;
    /** Ids for all plates created or updated via plate tools & lab auto runs in the worksheet.
     * Leveraged for plate summary data frame. */
    availablePlateIdsToSummarize: Array<string>;
    resultSchemaIds: Array<string>;
    entitySchemaIds: Array<string>;
    labAutoDatasetIds: Array<string>;
    positionsPlateIds: Array<string>;
    contentsPlateIds: Array<string>;
    positionsPlateRecordSpecs: Array<PlateRecordSpec>;
    contentsPlateRecordSpecs: Array<PlateRecordSpec>;
    positionsPlateDesignSpecs: Array<PlateDesignSpec>;
    contentsPlateDesignSpecs: Array<PlateDesignSpec>;

    analysisTemplateConfiguration?: ProcedureAnalysisTemplateConfiguration;
  }
>;

export function isWorksheetSeedDataConfigValid({data}: WorksheetSeedDataConfig): boolean {
  return (
    data.shouldIncludeConditionsDataFrame ||
    data.shouldIncludeDataParameterDataFrame ||
    data.shouldIncludeMaterialInputDataFrame ||
    data.shouldIncludeMaterialOutputDataFrame ||
    data.shouldIncludeEquipmentDataFrame ||
    data.shouldIncludeTestOrderDataFrame ||
    data.shouldIncludePreviousTestOrderDataFrame ||
    data.plateSchemaIdsToSummarize.length > 0 ||
    data.resultSchemaIds.length > 0 ||
    data.entitySchemaIds.length > 0 ||
    data.labAutoDatasetIds.length > 0 ||
    data.positionsPlateIds.length > 0 ||
    data.contentsPlateIds.length > 0 ||
    data.positionsPlateRecordSpecs.length > 0 ||
    data.contentsPlateRecordSpecs.length > 0 ||
    data.positionsPlateDesignSpecs.length > 0 ||
    data.contentsPlateDesignSpecs.length > 0 ||
    data.analysisTemplateConfiguration != null
  );
}

export function WorksheetSeedDataConfigEditor({
  value,
  onChange,
}: {
  value: WorksheetSeedDataConfig;
  onChange: (value: WorksheetSeedDataConfig) => void;
}): JSX.Element {
  const worksheetId = value.data.worksheetId;
  const {analysisSources, error, isLoading} = useAnalysisSourcesDuringExecution(worksheetId, true);

  function handleChangeSelectedSources(selectedSources: SelectedSources): void {
    onChange({
      type: SeedDataConfigType.WORKSHEET,
      data: {
        ...pick(
          value.data,
          'worksheetId',
          'sentToAnalysisTimestamp',
          'availablePlateIdsToSummarize',
          'analysisTemplateConfiguration'
        ),
        shouldIncludeConditionsDataFrame: selectedSources.conditions,
        shouldIncludeDataParameterDataFrame: selectedSources.dataParameters,
        shouldIncludeMaterialInputDataFrame: selectedSources.materialInputs,
        shouldIncludeMaterialOutputDataFrame: selectedSources.materialOutputs,
        shouldIncludeEquipmentDataFrame: selectedSources.equipment,
        shouldIncludeTestOrderDataFrame: selectedSources.testOrders,
        shouldIncludePreviousTestOrderDataFrame: selectedSources.previousTestOrders,
        plateSchemaIdsToSummarize: selectedSources.plateSchemaIds,
        resultSchemaIds: selectedSources.resultSchemaIds,
        entitySchemaIds: selectedSources.entitySchemaIds,
        labAutoDatasetIds: selectedSources.labAutoDatasetIds,
        positionsPlateIds: selectedSources.positionsPlateSourceIds.map((plateSourceIdentifier) =>
          parsePlateSourceIdentifier(plateSourceIdentifier)
        ),
        contentsPlateIds: selectedSources.contentsPlateSourceIds.map((plateSourceIdentifier) =>
          parsePlateSourceIdentifier(plateSourceIdentifier)
        ),
        positionsPlateRecordSpecs: selectedSources.positionsPlateRecordIds.map((recordId) =>
          parsePlateRecordSourceIdentifier(recordId)
        ),
        contentsPlateRecordSpecs: selectedSources.contentsPlateRecordIds.map((recordId) =>
          parsePlateRecordSourceIdentifier(recordId)
        ),
        positionsPlateDesignSpecs: selectedSources.positionsPlateDesignIds.map((recordId) =>
          parsePlateDesignSourceIdentifier(recordId)
        ),
        contentsPlateDesignSpecs: selectedSources.contentsPlateDesignIds.map((recordId) =>
          parsePlateDesignSourceIdentifier(recordId)
        ),
      },
    });
  }

  useEffect(() => {
    if (analysisSources != null) {
      const availablePlateIdsToSummarize = analysisSources.sourcesPerStep.flatMap((stepSources) =>
        stepSources.analysisInputs.plates.map((plate) => plate.plateId)
      );
      const newValue = {...value, data: {...value.data, availablePlateIdsToSummarize}};
      if (!isEqual(value.data.availablePlateIdsToSummarize, availablePlateIdsToSummarize)) {
        onChange(newValue);
      }
    }
  }, [analysisSources, value, onChange]);

  return (
    <>
      <FieldLabel label="Add datasets to analysis" />
      {BenchlingConfig.STUDIES_ENABLE_OFFLINE_RESULTS_IN_ANALYSIS && (
        <Text size="md" color="dimmed" mb="sm">
          Offline results are included in worksheet result datasets.
        </Text>
      )}
      {error ? (
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Some analysis sources are still loading"
          supportingInfo="Please re-open this modal after your worksheet has finished saving."
        />
      ) : isLoading ? (
        <CenteredHorizontalThrobber />
      ) : analysisSources ? (
        <ProcedureMethodAnalysisSourceTable
          selectedSources={{
            conditions: value.data.shouldIncludeConditionsDataFrame,
            dataParameters: value.data.shouldIncludeDataParameterDataFrame,
            materialInputs: value.data.shouldIncludeMaterialInputDataFrame,
            materialOutputs: value.data.shouldIncludeMaterialOutputDataFrame,
            equipment: value.data.shouldIncludeEquipmentDataFrame,
            testOrders: value.data.shouldIncludeTestOrderDataFrame,
            previousTestOrders: value.data.shouldIncludePreviousTestOrderDataFrame,
            plateSchemaIds: value.data.plateSchemaIdsToSummarize,
            resultSchemaIds: value.data.resultSchemaIds,
            entitySchemaIds: value.data.entitySchemaIds,
            labAutoDatasetIds: value.data.labAutoDatasetIds,
            positionsPlateSourceIds: value.data.positionsPlateIds,
            contentsPlateSourceIds: value.data.contentsPlateIds,
            positionsPlateRecordIds: value.data.positionsPlateRecordSpecs.map((spec) =>
              getPlateRecordSourceIdentifier(spec, PlateDatasetType.POSITIONS)
            ),
            contentsPlateRecordIds: value.data.contentsPlateRecordSpecs.map((spec) =>
              getPlateRecordSourceIdentifier(spec, PlateDatasetType.CONTENTS)
            ),
            positionsPlateDesignIds: value.data.positionsPlateDesignSpecs.map((spec) =>
              getPlateDesignSourceIdentifier(spec, PlateDatasetType.POSITIONS)
            ),
            contentsPlateDesignIds: value.data.contentsPlateDesignSpecs.map((spec) =>
              getPlateDesignSourceIdentifier(spec, PlateDatasetType.CONTENTS)
            ),
          }}
          analysisSourcesOrigin={AnalysisSourcesOrigin.EXECUTION}
          availableSourcesPerStep={analysisSources.sourcesPerStep}
          availableParameterTypes={analysisSources.availableParameterTypes}
          setSelectedSources={handleChangeSelectedSources}
          isSelectorHidden={false}
          multiSelect={true}
          includeEmptyDataSources={false}
          // Hide used in analysis column since that is only relevant for templates in config
          isColumnHidden={(accessorKey) => accessorKey === 'usedInAnalysis'}
        />
      ) : null}
    </>
  );
}
